// @flow
import React from 'react'
import ReactDOM from 'react-dom'
import { Link } from 'gatsby'
import { Manager, Reference, Popper } from 'react-popper'
import { CSSTransition } from 'react-transition-group'
import Icon from './icon'
import styles from './dropdown.module.css'

type Props = {
  links: Array<
    | {|
        name: string,
        path: string,
        isDisabled?: boolean,
      |}
    | string,
  >,
  children: ({
    referenceProps: {|
      ref: React$Ref<any>,
      className: string,
      onMouseEnter: () => void,
      onMouseLeave: () => void,
      onFocus: () => void,
      onBlur: () => void,
      children: React$Node,
    |},
  }) => React$Node,
}

const TIMEOUT = 200

function Dropdown({ links, children }: Props) {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  const [transitionIn, setTransitionIn] = React.useState<boolean>(false)
  const closeTimer = React.useRef()

  function handleOpen() {
    clearTimeout(closeTimer.current)
    setIsOpen(true)
    setTransitionIn(true)
  }
  function handleClose() {
    setTransitionIn(false)
    closeTimer.current = setTimeout(() => {
      setIsOpen(false)
    }, TIMEOUT)
  }

  const popperEl =
    typeof document !== 'undefined' ? document.querySelector('#popper') : null

  return (
    <Manager>
      <Reference>
        {({ ref }) =>
          children({
            referenceProps: {
              ref,
              className: styles.reference,
              onMouseEnter: handleOpen,
              onMouseLeave: handleClose,
              onFocus: handleOpen,
              onBlur: handleClose,
              children: (
                <Icon className={styles.icon} id="chevron-down" size={16} />
              ),
            },
          })
        }
      </Reference>
      {isOpen && popperEl != null
        ? ReactDOM.createPortal(
            <Popper
              placement="bottom-start"
              modifiers={{
                flip: {
                  enabled: false,
                },
                offset: {
                  enabled: true,
                  offset: '0, -5',
                },
              }}
            >
              {({ ref, style }) => (
                <CSSTransition
                  appear
                  in={transitionIn}
                  timeout={TIMEOUT}
                  classNames={styles}
                >
                  <ol
                    ref={ref}
                    className={styles.list}
                    style={style}
                    onMouseEnter={handleOpen}
                    onMouseLeave={handleClose}
                  >
                    {links.map((link) => {
                      if (typeof link === 'string') {
                        return <li key={link} className={styles.separator} />
                      }
                      if (link.isDisabled) {
                        return (
                          <li key={link.path}>
                            <span className={styles.label}>{link.name}</span>
                          </li>
                        )
                      }
                      if (link.path.startsWith('/')) {
                        return (
                          <li key={link.path}>
                            <Link
                              className={styles.link}
                              activeClassName={styles.active}
                              to={link.path}
                            >
                              {link.name}
                            </Link>
                          </li>
                        )
                      }
                      return (
                        <li key={link.path}>
                          <a className={styles.link} href={link.path}>
                            {link.name}
                          </a>
                        </li>
                      )
                    })}
                  </ol>
                </CSSTransition>
              )}
            </Popper>,
            popperEl,
          )
        : null}
    </Manager>
  )
}

export default Dropdown
